import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { encryptJournal, decryptJournals } from '../lib/encryption';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, BookOpen, Calendar, Save, Lock } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JournalSection = () => {
  const { getAuthHeaders, isAuthenticated, user, encryptionPassword, logout } = useAuth();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingJournal, setEditingJournal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    scripture_reference: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState('');
  const [reAuthenticating, setReAuthenticating] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      // Check if encryption password is missing (page refresh scenario)
      if (!encryptionPassword) {
        setShowPasswordPrompt(true);
        setLoading(false);
      } else {
        fetchJournals();
      }
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, encryptionPassword]);

  const fetchJournals = async () => {
    try {
      const response = await axios.get(`${API}/journals`, {
        headers: getAuthHeaders()
      });
      
      // Decrypt journals if we have the encryption password
      if (encryptionPassword && user) {
        const decrypted = await decryptJournals(response.data, encryptionPassword, user.id);
        setJournals(decrypted);
      } else {
        // Fallback: show encrypted content (shouldn't happen in normal flow)
        setJournals(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch journals:', error);
      toast.error('Failed to load journals');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (journal = null) => {
    if (journal) {
      setEditingJournal(journal);
      setFormData({
        title: journal.title,
        content: journal.content,
        scripture_reference: journal.scripture_reference || ''
      });
    } else {
      setEditingJournal(null);
      setFormData({ title: '', content: '', scripture_reference: '' });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingJournal(null);
    setFormData({ title: '', content: '', scripture_reference: '' });
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    if (!encryptionPassword || !user) {
      toast.error('Encryption key not available. Please log in again.');
      return;
    }

    setSaving(true);
    try {
      // Encrypt content before sending to server
      const encryptedContent = await encryptJournal(formData.content, encryptionPassword, user.id);
      const payload = {
        ...formData,
        content: encryptedContent
      };

      if (editingJournal) {
        await axios.put(
          `${API}/journals/${editingJournal.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Journal updated');
      } else {
        await axios.post(
          `${API}/journals`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Journal created');
      }
      await fetchJournals();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save journal:', error);
      toast.error('Failed to save journal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (journalId) => {
    if (!window.confirm('Are you sure you want to delete this journal entry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/journals/${journalId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Journal deleted');
      await fetchJournals();
    } catch (error) {
      console.error('Failed to delete journal:', error);
      toast.error('Failed to delete journal');
    }
  };

  const handleReAuthenticate = async () => {
    if (!reAuthPassword.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setReAuthenticating(true);
    try {
      // Verify password by attempting login
      const response = await axios.post(`${API}/auth/login`, {
        email: user.email,
        password: reAuthPassword
      });

      // If successful, the AuthContext will update with the new token and password
      // But we need to manually set the encryption password since we're already logged in
      // Instead, let's just force a full re-login
      toast.success('Password verified. Reloading journals...');
      logout();
      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Re-authentication failed:', error);
      toast.error('Incorrect password. Please try again.');
      setReAuthPassword('');
    } finally {
      setReAuthenticating(false);
    }
  };

  const handleLogoutFromPrompt = () => {
    logout();
    window.location.href = '/login';
  };

  if (!isAuthenticated) {
    return (
      <Card data-testid="journal-section-login">
        <CardContent className="p-8 text-center">
          <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            Log in to start journaling your spiritual reflections.
          </p>
          <Button asChild>
            <a href="/login">Log In to Journal</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Password Re-authentication Prompt */}
      <AlertDialog open={showPasswordPrompt} onOpenChange={setShowPasswordPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Encryption Key Required
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your journals are encrypted for privacy. Please log in again to access your encrypted content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={handleLogoutFromPrompt}>
              Log In Again
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="space-y-6" data-testid="journal-section">
        <div className="flex justify-between items-center">
          <h3 className="font-serif text-2xl font-semibold">My Journal</h3>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()} 
              className="btn-hover-lift"
              data-testid="journal-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editingJournal ? 'Edit Journal Entry' : 'New Journal Entry'}
              </DialogTitle>
              <DialogDescription>
                Record your reflections on Scripture and what God is teaching you.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="journal-title">Title</Label>
                <Input
                  id="journal-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter a title for your reflection"
                  data-testid="journal-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="journal-scripture">Scripture Reference (optional)</Label>
                <Input
                  id="journal-scripture"
                  value={formData.scripture_reference}
                  onChange={(e) => setFormData({ ...formData, scripture_reference: e.target.value })}
                  placeholder="e.g., Romans 8:28"
                  data-testid="journal-scripture-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="journal-content">Reflection</Label>
                <Textarea
                  id="journal-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="What is God teaching you? How does this truth apply to your life?"
                  rows={6}
                  data-testid="journal-content-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="journal-save-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
                <div className="h-4 bg-muted rounded w-full mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : journals.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No journal entries yet. Start recording your spiritual reflections!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {journals.map((journal) => (
            <Card 
              key={journal.id} 
              className="journal-entry hover:shadow-md transition-shadow"
              data-testid={`journal-entry-${journal.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="font-serif text-lg">{journal.title}</CardTitle>
                    {journal.scripture_reference && (
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <BookOpen className="h-3 w-3" />
                        {journal.scripture_reference}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(journal)}
                      aria-label="Edit journal"
                      data-testid={`journal-edit-${journal.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(journal.id)}
                      className="text-destructive hover:text-destructive"
                      aria-label="Delete journal"
                      data-testid={`journal-delete-${journal.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 whitespace-pre-wrap leading-relaxed">
                  {journal.content}
                </p>
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(journal.created_at), 'MMMM d, yyyy')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </>
  );
};

export default JournalSection;
